import { TranslocoService } from '@jsverse/transloco';
import { combineLatest, map, Observable } from 'rxjs';

import { DeviceUiOutputsDeviceBehaviorPlugin } from '@shure/device-ui/outputs/data-access';
import {
	DeviceUiSchematicsDeviceBehaviorPlugin,
	SchematicsDeviceChannelDspKind,
	SchematicsDeviceChannel
} from '@shure/device-ui/schematics/data-access';
import { DeviceUiSettingsFactoryResetBehaviorPlugin } from '@shure/device-ui/settings/factory-reset/data-access';
import { FactoryResetText } from '@shure/device-ui/settings/factory-reset/data-access';
import { DspType, DspChannelType, DspCopyChannelProperty } from '@shure/device-ui/shared/dsp/data-access';
import { DeviceUiPage, DeviceUiRoute, DeviceUiSharedDeviceBehaviorPlugin } from '@shure/device-ui/shared/services';
import { DeviceUiShellDeviceBehaviorPlugin } from '@shure/device-ui/shell/data-access';
import { AnalogOutputMode, ChannelGroup, DeviceModel } from '@shure/shared/angular/data-access/system-api/models';
import { DeviceBehaviorPlugin, DeviceNameBehaviorPlugin } from '@shure/shared/angular/device-behavior/core';
import { CoverageImage, CoverageImageProvider } from '@shure/shared/angular/features/device-coverage/ui';
import {
	LightsSettingsDeviceBehaviorPlugin,
	LightsSettingsSupportType
} from '@shure/shared/angular/features/device-lights-settings/data-access';
import { shUseTranslation } from '@shure/shared/angular/utils/i18n';

@shUseTranslation('scDevUiSharedDevBehavior', (lang) => import(`../i18n/${lang}`))
export class MxnAmpDeviceUiDeviceBehaviorPlugin
	implements
		DeviceBehaviorPlugin,
		DeviceUiShellDeviceBehaviorPlugin,
		DeviceUiOutputsDeviceBehaviorPlugin,
		DeviceUiSchematicsDeviceBehaviorPlugin,
		DeviceUiSettingsFactoryResetBehaviorPlugin,
		LightsSettingsDeviceBehaviorPlugin,
		DeviceNameBehaviorPlugin,
		CoverageImageProvider,
		DeviceUiSharedDeviceBehaviorPlugin
{
	public readonly models: DeviceModel[] = [DeviceModel.MXNAMP];

	constructor(protected readonly transloco: TranslocoService) {}

	public getAvailablePages$(
		_model: DeviceModel,
		_options?: { virtual?: boolean | undefined; autoCoverageMode?: boolean | undefined } | undefined
	): Observable<DeviceUiPage[] | undefined> {
		return combineLatest([
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.schematic'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.inputs'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.matrix-mixer'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.outputs'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.presets'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.settings')
		]).pipe(
			map(([schematicLabel, inputsLabel, matrixMixerLabel, outputsLabel, presetsLabel, settingsLabel]) => [
				{
					route: DeviceUiRoute.Schematic,
					label: schematicLabel
				},
				{
					route: DeviceUiRoute.Inputs,
					label: inputsLabel
				},
				{
					route: DeviceUiRoute.Matrixmixer,
					label: matrixMixerLabel
				},
				{
					route: DeviceUiRoute.Outputs,
					label: outputsLabel
				},
				{
					route: DeviceUiRoute.Presets,
					label: presetsLabel
				},
				{
					route: DeviceUiRoute.Settings,
					label: settingsLabel
				}
			])
		);
	}

	public getDefaultRoute(_model: DeviceModel): DeviceUiRoute | undefined {
		return DeviceUiRoute.Schematic;
	}

	public getResetText(_model: DeviceModel): FactoryResetText {
		return {
			tab$: this.transloco.selectTranslate('scDevUiSharedDevBehavior.settings.factory-reset'),
			title: this.transloco.translate('scDevUiSharedDevBehavior.settings.factory-reset'),
			button: this.transloco.translate(
				'scDevUiSharedDevBehavior.settings.factory-reset-page.restore-factory-defaults'
			)
		};
	}

	public hasDanteSendsFromAnalog(_model: DeviceModel): boolean {
		return true;
	}

	public getOutputChannelDspSortingMap(_model: DeviceModel): Map<DspType, number> {
		return new Map<DspType, number>([
			[DspType.Delay, 1],
			[DspType.Peq, 2],
			[DspType.Limiter, 3]
		]);
	}

	public getLightsSettingsSupportType(): LightsSettingsSupportType {
		return LightsSettingsSupportType.SupportedWithoutImage;
	}

	public getDeviceInputChannelsSortingMap(_model: DeviceModel): Map<ChannelGroup, number> | undefined {
		return new Map<ChannelGroup, number>([
			[ChannelGroup.DanteInput, 0],
			[ChannelGroup.DanteAnalogInput, 1],
			[ChannelGroup.Generic, 2],
			[ChannelGroup.SignalGeneratorInput, 3]
		]);
	}

	public getInputChannelGroups(): ChannelGroup[] {
		return [ChannelGroup.DanteInput, ChannelGroup.DanteAnalogInput];
	}

	public getOutputChannelGroups(_mode: DeviceModel, analogOutputMode: AnalogOutputMode): ChannelGroup[] {
		return analogOutputMode === AnalogOutputMode.LowImpedance4Channels ||
			analogOutputMode === AnalogOutputMode.LowImpedance2Channels
			? [ChannelGroup.DanteOutput, ChannelGroup.AnalogOutput]
			: [ChannelGroup.DanteOutput, ChannelGroup.Analog70VOutput];
	}

	public shouldResetOuputGroupIndex(_model: DeviceModel): boolean {
		return true;
	}

	public isCompressorCopyPasteSupported(_model: DeviceModel): boolean {
		return true;
	}

	public getSchematicOutputChannelDspSortingMap(_model: DeviceModel): Map<SchematicsDeviceChannelDspKind, number> {
		return new Map<SchematicsDeviceChannelDspKind, number>([
			['delay', 1],
			['equalizer', 2],
			['limiter', 3]
		]);
	}

	public getChannelCopyPasteProperties(
		_model: DeviceModel,
		channelType: DspChannelType,
		selectedChannels: SchematicsDeviceChannel[]
	): DspCopyChannelProperty[] {
		if (channelType === DspChannelType.Input && selectedChannels.every((c) => !!c.features.inputSource)) {
			return ['muted', 'gain', 'inputSource'];
		} else if (
			channelType === DspChannelType.Output &&
			selectedChannels.every((c) => !!c.features.passiveSpeakers)
		) {
			return ['muted', 'gain', 'passiveSpeakers'];
		} else if (channelType === DspChannelType.Output && selectedChannels.every((c) => !!c.features.outputPath)) {
			return ['muted', 'gain', 'outputPath'];
		}
		return ['muted', 'gain'];
	}

	public getDeviceNameMaxLength(_model: DeviceModel): number | undefined {
		return 31;
	}

	public getDirectChannelGroups(_model: DeviceModel): undefined {
		return undefined;
	}

	public getAutomixedChannelGroups(): undefined {
		return undefined;
	}

	public getCoverageImage(): CoverageImage {
		return {
			live: 'device-mxn-amp-back.svg',
			virtual: 'device-mxn-amp-back-line.svg',
			isImageRound: false
		};
	}
}
